import React, { useMemo, useState } from 'react'
import { collection, limit, onSnapshot, query, where } from 'firebase/firestore'
import { getAuth } from 'firebase/auth'
import { httpsCallable } from 'firebase/functions'
import { Bell, Trash2, X } from 'lucide-react'
import { db, functions } from '../firebase-config'
import TicketConversation from './TicketConversation'

function fmt(ts) {
  const d = ts?.toDate ? ts.toDate() : new Date(ts)
  if (!d || Number.isNaN(d.getTime())) return '-'
  return d.toLocaleString('es-AR')
}

export default function UserTicketsModal({ onClose }) {
  const [items, setItems] = useState([])
  const [selected, setSelected] = useState(null)
  const [busy, setBusy] = useState('')

  React.useEffect(() => {
    const uid = getAuth().currentUser?.uid
    if (!uid) return undefined
    const q = query(collection(db, 'tickets'), where('userUid', '==', uid), limit(30))
    const unsub = onSnapshot(q, (snap) => {
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const ta = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0
          const tb = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0
          return tb - ta
        })
      setItems(rows)
      setSelected((prev) => prev || rows[0] || null)
    })
    return () => unsub()
  }, [])

  const visibleTickets = useMemo(() => {
    const now = Date.now()
    const tenDays = 10 * 24 * 60 * 60 * 1000
    return items.filter((t) => {
      if (t.estado !== 'resuelto') return true
      const closedDate = t.closedAt?.toDate ? t.closedAt.toDate() : (t.updatedAt?.toDate ? t.updatedAt.toDate() : null)
      if (!closedDate) return true
      return now - closedDate.getTime() <= tenDays
    })
  }, [items])

  const closeTicket = async (ticketId) => {
    setBusy(ticketId)
    try {
      const callClose = httpsCallable(functions, 'closeOwnTicket')
      await callClose({ ticketId })
    } finally {
      setBusy('')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="relative w-full max-w-5xl rounded-3xl border border-white/20 bg-white/10 p-4 backdrop-blur-2xl" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose} className="absolute right-4 top-4 rounded-lg border border-white/20 bg-white/10 p-2 text-slate-200 hover:bg-white/20">
          <X className="h-4 w-4" />
        </button>
        <div className="mb-4 flex items-center gap-2 text-slate-100">
          <Bell className="h-4 w-4 text-cyan-300" />
          <h3 className="text-lg font-semibold">Mis tickets de soporte</h3>
        </div>

        <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
          <div className="max-h-[65vh] space-y-2 overflow-auto pr-1">
            {visibleTickets.length === 0 && <p className="text-sm text-slate-300">No tienes tickets activos.</p>}
            {visibleTickets.map((t) => (
              <div key={t.id} className={`rounded-xl border px-3 py-2 ${selected?.id === t.id ? 'border-cyan-300/60 bg-cyan-500/10' : 'border-white/15 bg-white/5'}`}>
                <button type="button" onClick={() => setSelected(t)} className="w-full text-left">
                  <p className="text-xs font-semibold text-slate-200">{t.ticketCode || t.id}</p>
                  <p className="text-sm font-semibold text-white">{t.asunto || 'Sin asunto'}</p>
                  <p className="text-xs text-slate-300">{fmt(t.createdAt)} · {t.estado || 'abierto'}</p>
                </button>
                {t.estado !== 'resuelto' && (
                  <button
                    type="button"
                    onClick={() => closeTicket(t.id)}
                    disabled={busy === t.id}
                    className="mt-2 inline-flex items-center gap-1 rounded-lg border border-rose-300/40 bg-rose-500/20 px-2 py-1 text-xs text-rose-100 hover:bg-rose-500/30 disabled:opacity-60"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Eliminar ticket
                  </button>
                )}
              </div>
            ))}
          </div>

          <div>
            {selected ? (
              <TicketConversation ticket={selected} compact />
            ) : (
              <div className="rounded-xl border border-white/15 bg-white/5 p-4 text-sm text-slate-300">Selecciona un ticket para ver el historial.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
